import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Leaf, Camera, Upload, Crop, Sparkles, Search, Layers, Download, ArrowLeft, WifiOff, Share2, Link2 as LinkIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import aboutContent from '../i18n/aboutContent';

export const AboutPage = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const c = aboutContent[language] || aboutContent.en;

  const stepIcons = [Leaf, Camera, Crop, Sparkles, Upload, Search, Layers, Download];

  // Deep-link support: when arriving at /about#<id> (e.g. the Settings
  // or Share "Learn more" links), smooth-scroll to that section.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash) {
      const id = window.location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
    }
  }, []);

  const toc = [
    { id: 'what', label: c.whatTitle },
    { id: 'how', label: c.howTitle },
    { id: 'tips', label: c.tipsTitle },
    { id: 'ai', label: c.aiTitle },
    { id: 'local', label: c.localTitle },
    { id: 'offline-sync', label: t('offlineHelpTitle') || 'Offline & Sync' },
    { id: 'sharing', label: t('aboutSharingTitle') || 'Sharing your plants' },
  ];

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Copy a deep-link to a section (e.g. …/about#sharing) to the clipboard.
  const CopyAnchor = ({ id }) => (
    <button
      type="button"
      onClick={() => {
        const url = `${window.location.origin}/about#${id}`;
        try {
          navigator.clipboard.writeText(url)
            .then(() => toast.success(t('linkCopied')))
            .catch(() => toast.error('Copy failed'));
        } catch {
          toast.error('Copy failed');
        }
      }}
      className="ml-auto flex-shrink-0 opacity-40 hover:opacity-100 focus:opacity-100 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500/40 rounded"
      style={{ color: 'hsl(143, 28%, 40%)' }}
      aria-label={t('copyLinkToSection')}
      title={t('copyLinkToSection')}
      data-testid={`about-copy-link-${id}`}
    >
      <LinkIcon className="w-4 h-4" aria-hidden="true" />
    </button>
  );

  // Slim reading-progress bar across the top of the guide.
  const [readProgress, setReadProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setReadProgress(max > 0 ? Math.min(100, (window.scrollY / max) * 100) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll-spy: highlight the TOC link whose section is currently in view.
  const [activeSection, setActiveSection] = useState('what');
  useEffect(() => {
    const ids = ['what', 'how', 'tips', 'ai', 'local', 'offline-sync', 'sharing'];
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (els.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveSection(visible[0].target.id);
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <main className="min-h-screen" style={{ backgroundColor: 'hsl(82, 55%, 95%)' }}>
      {/* Reading progress bar */}
      <div
        className="fixed top-0 left-0 right-0 z-50 h-1"
        style={{ backgroundColor: 'hsl(82, 40%, 88%)' }}
        role="progressbar"
        aria-label={t('readingProgress') || 'Reading progress'}
        aria-valuenow={Math.round(readProgress)}
        aria-valuemin={0}
        aria-valuemax={100}
        data-testid="about-reading-progress"
      >
        <div
          className="h-full transition-[width] duration-150 ease-out"
          style={{ width: `${readProgress}%`, backgroundColor: 'hsl(143, 45%, 42%)' }}
        />
      </div>

      <div className="max-w-2xl mx-auto px-4 py-10">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          style={{ color: 'hsl(143, 28%, 25%)' }}
          data-testid="about-back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('back')}
        </Button>

        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
            <img src="/monstera-login-v3.png" alt="Monstera leaf" width="64" height="64" style={{ objectFit: 'contain' }} />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mb-3" style={{ color: 'hsl(143, 28%, 25%)' }}>
            {t('appName')}
          </h1>
          <p className="text-base" style={{ color: 'hsl(125, 12%, 25%)' }}>
            {c.subtitle}
          </p>
        </div>

        {/* Table of contents — inline, tappable jump links */}
        <nav
          className="rounded-2xl p-5 mb-6 shadow-sm"
          style={{ backgroundColor: 'white', border: '1px solid hsl(143, 28%, 90%)' }}
          aria-label={t('aboutTocTitle') || 'On this page'}
          data-testid="about-toc"
        >
          <h2 className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: 'hsl(143, 28%, 35%)' }}>
            {t('aboutTocTitle') || 'On this page'}
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            {toc.map((item) => {
              const isActive = activeSection === item.id;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => scrollToSection(item.id)}
                    aria-current={isActive ? 'true' : undefined}
                    data-active={isActive ? 'true' : undefined}
                    className={`w-full flex items-center gap-2 text-left text-sm px-2 py-1.5 rounded-md transition-all hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500/40 ${isActive ? 'font-semibold' : ''}`}
                    style={{
                      color: isActive ? 'hsl(143, 45%, 26%)' : 'hsl(143, 45%, 38%)',
                      backgroundColor: isActive ? 'hsl(143, 45%, 92%)' : 'transparent',
                    }}
                    data-testid={`about-toc-link-${item.id}`}
                  >
                    <span
                      className="flex-shrink-0 rounded-full transition-all"
                      style={{
                        width: isActive ? '6px' : '4px',
                        height: isActive ? '6px' : '4px',
                        backgroundColor: isActive ? 'hsl(143, 50%, 40%)' : 'hsl(143, 28%, 75%)',
                      }}
                      aria-hidden="true"
                    />
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* What is this app */}
        <section id="what" className="rounded-2xl p-6 mb-6 shadow-sm scroll-mt-6" style={{ backgroundColor: 'white', border: '1px solid hsl(143, 28%, 90%)' }}>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-lg font-semibold" style={{ color: 'hsl(143, 28%, 25%)' }}>{c.whatTitle}</h2>
            <CopyAnchor id="what" />
          </div>
          <p className="text-sm leading-relaxed mb-3" style={{ color: 'hsl(125, 12%, 25%)' }}>
            {c.whatP1}
          </p>
          <p className="text-sm leading-relaxed mb-3" style={{ color: 'hsl(125, 12%, 25%)' }}>
            {c.whatP2}
          </p>
        </section>

        {/* How to use */}
        <section id="how" className="rounded-2xl p-6 mb-6 shadow-sm scroll-mt-6" style={{ backgroundColor: 'white', border: '1px solid hsl(143, 28%, 90%)' }}>
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-lg font-semibold" style={{ color: 'hsl(143, 28%, 25%)' }}>{c.howTitle}</h2>
            <CopyAnchor id="how" />
          </div>
          <div className="space-y-5">
            {c.steps.map((step, idx) => {
              const Icon = stepIcons[idx] || Leaf;
              return (
                <Step key={idx} icon={<Icon className="w-4 h-4" />} number={String(idx + 1)} title={step.title}>
                  {step.desc}
                </Step>
              );
            })}
          </div>
        </section>

        {/* Tips */}
        <section id="tips" className="rounded-2xl p-6 mb-6 shadow-sm scroll-mt-6" style={{ backgroundColor: 'white', border: '1px solid hsl(143, 28%, 90%)' }}>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-lg font-semibold" style={{ color: 'hsl(143, 28%, 25%)' }}>{c.tipsTitle}</h2>
            <CopyAnchor id="tips" />
          </div>
          <ul className="space-y-2 text-sm" style={{ color: 'hsl(125, 12%, 25%)' }}>
            {c.tips.map((tip, idx) => (
              <li key={idx} className="flex gap-2">
                <span style={{ color: 'hsl(143, 50%, 45%)' }}>-</span>
                <span><strong>{tip.bold}</strong> {tip.text}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* AI Accuracy Disclaimer */}
        <section id="ai" className="rounded-2xl p-6 mb-6 shadow-sm scroll-mt-6" style={{ backgroundColor: 'hsl(40, 90%, 97%)', border: '1px solid hsl(40, 70%, 85%)' }}>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-lg font-semibold" style={{ color: 'hsl(40, 50%, 30%)' }}>{c.aiTitle}</h2>
            <CopyAnchor id="ai" />
          </div>
          <p className="text-sm leading-relaxed mb-3" style={{ color: 'hsl(40, 30%, 25%)' }}>
            {c.aiP1}
          </p>
          <p className="text-sm leading-relaxed" style={{ color: 'hsl(40, 30%, 25%)' }}>
            {c.aiP2}
          </p>
        </section>

        {/* Local Mode */}
        <section id="local" className="rounded-2xl p-6 mb-6 shadow-sm scroll-mt-6" style={{ backgroundColor: 'white', border: '1px solid hsl(143, 28%, 90%)' }}>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-lg font-semibold" style={{ color: 'hsl(143, 28%, 25%)' }}>{c.localTitle}</h2>
            <CopyAnchor id="local" />
          </div>
          <p className="text-sm leading-relaxed mb-3" style={{ color: 'hsl(125, 12%, 25%)' }}>
            {c.localDesc}
          </p>
          <ul className="space-y-2 text-sm" style={{ color: 'hsl(125, 12%, 25%)' }}>
            {c.localItems.map((item, idx) => (
              <li key={idx} className="flex gap-2">
                <span style={{ color: 'hsl(40, 90%, 50%)' }}>-</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 rounded-xl p-4" style={{ backgroundColor: 'hsl(82, 55%, 96%)', border: '1px solid hsl(143, 28%, 88%)' }}>
            <p className="text-sm font-semibold mb-2" style={{ color: 'hsl(143, 28%, 25%)' }}>{c.localTipTitle}</p>
            <p className="text-sm leading-relaxed" style={{ color: 'hsl(125, 12%, 25%)' }}>
              {c.localTipDesc}
            </p>
          </div>
        </section>

        {/* Offline & Sync — deep-link target for the Settings "Learn more" link */}
        <section
          id="offline-sync"
          className="rounded-2xl p-6 mb-6 shadow-sm scroll-mt-6"
          style={{ backgroundColor: 'white', border: '1px solid hsl(143, 28%, 90%)' }}
          data-testid="about-offline-sync-section"
        >
          <h2 className="flex items-center gap-2 text-lg font-semibold mb-3" style={{ color: 'hsl(143, 28%, 25%)' }}>
            <WifiOff className="w-5 h-5" aria-hidden="true" />
            {t('offlineHelpTitle') || 'Offline & Sync'}
            <CopyAnchor id="offline-sync" />
          </h2>
          <p className="text-sm leading-relaxed" style={{ color: 'hsl(125, 12%, 25%)' }}>
            {t('offlineHelpBody') || 'Offline you can browse and edit, archive or delete plants — those changes queue and sync automatically when you reconnect. Adding a brand-new plant needs a connection.'}
          </p>
        </section>

        {/* Sharing / grow-along — deep-link target for the Share modal's "Learn more" */}
        <section
          id="sharing"
          className="rounded-2xl p-6 mb-6 shadow-sm scroll-mt-6"
          style={{ backgroundColor: 'white', border: '1px solid hsl(143, 28%, 90%)' }}
          data-testid="about-sharing-section"
        >
          <h2 className="flex items-center gap-2 text-lg font-semibold mb-3" style={{ color: 'hsl(143, 28%, 25%)' }}>
            <Share2 className="w-5 h-5" aria-hidden="true" />
            {t('aboutSharingTitle') || 'Sharing your plants'}
            <CopyAnchor id="sharing" />
          </h2>
          <p className="text-sm leading-relaxed" style={{ color: 'hsl(125, 12%, 25%)' }}>
            {t('aboutSharingBody') || 'Share a single plant as a rich image, or your whole library as a PDF — at a glance (photos + names), in full detail (a page per plant), or names only. Every library PDF includes a scannable QR code, a tappable link and your welcome note. Recipients open a read-only \u201cgrow-along\u201d view; nothing in your library changes and they can\u2019t edit it.'}
          </p>
        </section>

      </div>
    </main>
  );
};

const Step = ({ icon, number, title, children }) => (
  <div className="flex gap-3">
    <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
      style={{ backgroundColor: 'hsl(143, 28%, 90%)', color: 'hsl(143, 28%, 25%)' }}>
      {number}
    </div>
    <div className="min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <span style={{ color: 'hsl(143, 50%, 45%)' }}>{icon}</span>
        <h3 className="text-sm font-semibold" style={{ color: 'hsl(143, 28%, 25%)' }}>{title}</h3>
      </div>
      <p className="text-sm leading-relaxed" style={{ color: 'hsl(125, 12%, 25%)' }}>{children}</p>
    </div>
  </div>
);
